import React, { useState, useCallback, useEffect } from "react";
import FloatingCard from "../../../ui/FloatingCard";
import ScoringModeToggle from "../../live/components/ScoringModeToggle";
import InlinePlayerGrid from "../common/InlinePlayerGrid";
import PermissionBadge from "../common/PermissionBadge";
import BasicScoreBox from "../common/BasicScoreBox";
import { getBadmintonFormat, checkRallyGameWinner } from "../../../../utils/sportRules";

// ─── localStorage helpers ────────────────────────────────────────────────────
const BD_KEY = (id) => `badminton_games_${id}`;
const saveBD = (id, data) => { try { localStorage.setItem(BD_KEY(id), JSON.stringify(data)); } catch {} };
const loadBD = (id) => { try { const r = localStorage.getItem(BD_KEY(id)); return r ? JSON.parse(r) : null; } catch { return null; } };

/**
 * Read badminton game configuration from the match object.
 *
 * Match creation saves the chosen format as
 *   match_config.scoring_rules = { points_to_win, win_by_margin, max_points }
 *   match_config.game_rules    = { total_games }
 * Legacy / schedule-generated matches may keep the scoring values in
 * game_rules — those are used as fallbacks. Defaults follow BWF:
 * best of 3 games to 21, win by 2, capped at 30.
 */
const getBDConfig = (match) => {
  const { totalGames, ptsToWin, winMargin, maxPoints, gamesToWin } = getBadmintonFormat(match);
  return { totalGames, ptsToWin, winMargin, maxPoints, gamesToWin };
};

// Exported for tests
export { getBDConfig };

/**
 * Determine the winner of a badminton game.
 *
 * Rules:
 *   1. Normal win  : score >= ptsToWin AND lead >= winMargin
 *   2. Cap win     : either score >= maxPoints (30-29 → 30 wins; 30-30 impossible)
 *
 * Returns "p1" | "p2" | null
 */
const checkGameWon = (p1, p2, ptsToWin, winMargin, maxPoints) =>
  checkRallyGameWinner(p1, p2, ptsToWin, winMargin, maxPoints);

// Exported for tests
export { checkGameWon };

const BadmintonScoreboard = ({
  match,
  currentScore,
  isReadOnlyMode,
  connected,
  isMatchActive,
  onScoreIncrease,
  onScoreDecrease,
  onResetScore,
  onResetSetScore,
  onCompleteMatch,
  onStartMatch,
  isTimeoutActive,
  onToggleTimeout,
  matchDuration,
  scoringMode,
  onScoringModeChange,
  advancedSupported = false,
  announcerMuted,
  onAnnouncerToggleMute,
  onAnnouncerRepeat,
  // Inline advanced scoring: pass metrics + player lists so chips are shown
  // directly on the scoreboard — no +1 → modal two-step needed.
  quickMetrics = [],    // [{key, metric}] from sportScorer.getQuickMetrics()
  p1Players = [],       // Player objects for participant 1
  p2Players = [],       // Player objects for participant 2
  onDirectAction,       // legacy — kept for backward compat
  onPlayerSelect,       // (participant, player) => void — new inline flow
  // Game state sync — onGameComplete is called by the editor when a game
  // finishes; externalGameState is supplied by the host to keep viewer displays
  // in sync with the editor's game counter/scores via WebSocket.
  onGameComplete,
  externalGameState,
}) => {
  const matchId = match?.id;
  const { totalGames, ptsToWin, winMargin, maxPoints, gamesToWin } = getBDConfig(match);
  const isSingleGame = totalGames === 1;

  // ── Expand / maximized view (available to all users including viewers) ────
  const [isMaximized, setIsMaximized] = useState(false);

  // ── Game state (persisted per match) ─────────────────────────────────────
  const [gamesWon, setGamesWon] = useState(() => {
    const saved = loadBD(matchId);
    return saved?.gamesWon ?? { p1: 0, p2: 0 };
  });
  const [gameScores, setGameScores] = useState(() => {
    const saved = loadBD(matchId);
    return saved?.gameScores ?? []; // [{p1, p2}, ...]
  });
  const [currentGame, setCurrentGame] = useState(() => {
    const saved = loadBD(matchId);
    // Clamp to totalGames in case localStorage has a stale value from before
    // the fix that prevented currentGame from incrementing past the last game.
    return Math.min(saved?.currentGame ?? 1, totalGames);
  });
  const [matchWon, setMatchWon] = useState(() => {
    const saved = loadBD(matchId);
    return saved?.matchWon ?? false;
  });

  // null = live; N = viewing historical game N
  const [viewingGame, setViewingGame] = useState(null);

  // ── Sync game state from external source (WebSocket, for viewers) ─────────
  useEffect(() => {
    if (!externalGameState) return;
    // Only override local state when it differs — avoids unnecessary re-renders
    if (
      externalGameState.currentGame !== undefined &&
      (externalGameState.currentGame !== currentGame ||
        externalGameState.gamesWon?.p1 !== gamesWon.p1 ||
        externalGameState.gamesWon?.p2 !== gamesWon.p2)
    ) {
      setGamesWon(externalGameState.gamesWon ?? { p1: 0, p2: 0 });
      setGameScores(externalGameState.gameScores ?? []);
      setCurrentGame(externalGameState.currentGame);
      setMatchWon(externalGameState.matchWon ?? false);
    }
  // Intentionally depend only on externalGameState reference
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalGameState]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const p1Score = currentScore?.participant1 || 0;
  const p2Score = currentScore?.participant2 || 0;

  const gameWinner = checkGameWon(p1Score, p2Score, ptsToWin, winMargin, maxPoints);

  // ── Participant names ─────────────────────────────────────────────────────
  const dit    = (match?.matchConfig || match?.match_config)?.drop_in_teams;
  const p1Name = dit
    ? (dit.team_a?.name || "Player A")
    : (match?.participant_1_name || match?.participant1Name || "Player 1");
  const p2Name = dit
    ? (dit.team_b?.name || "Player B")
    : (match?.participant_2_name || match?.participant2Name || "Player 2");

  const gameWinners = gameScores.map((gs) =>
    gs.p1 > gs.p2 ? "p1" : gs.p2 > gs.p1 ? "p2" : "draw"
  );

  // Build the completionData override for a won match so
  // handleCompleteMatchWithModal uses the games-won counts (not the point score).
  const buildCompletionData = useCallback((gw, gs) => {
    const matchWinnerKey = gw.p1 >= gamesToWin ? "p1" : gw.p2 >= gamesToWin ? "p2" : null;
    if (!matchWinnerKey) return null;
    const p1Id   = match?.participant_1_id   || match?.participant1Id;
    const p2Id   = match?.participant_2_id   || match?.participant2Id;
    const p1Type = match?.participant_1_type || match?.participant1Type || "user";
    const p2Type = match?.participant_2_type || match?.participant2Type || "user";
    return {
      cancelled: false,
      winner: {
        id:   matchWinnerKey === "p1" ? p1Id   : p2Id,
        type: matchWinnerKey === "p1" ? p1Type : p2Type,
      },
      winnerName:        matchWinnerKey === "p1" ? p1Name : p2Name,
      participant1Score: gw.p1,
      participant2Score: gw.p2,
      // Full per-game breakdown — used by MatchDetails to render G1/G2/G3 pills
      gameScores: gs,
    };
  }, [gamesToWin, match, p1Name, p2Name]);

  // ── Game completion handler ───────────────────────────────────────────────
  // The header's Complete: a game that is won but not yet completed counts, so
  // pressing it right after the deciding point records the games (2–0), not
  // that game's points (21–0).
  const completionForHeader = () => {
    const countCurrent = !matchWon && (gameWinner === "p1" || gameWinner === "p2");
    if (!countCurrent) return buildCompletionData(gamesWon, gameScores);
    return buildCompletionData(
      { p1: gamesWon.p1 + (gameWinner === "p1" ? 1 : 0), p2: gamesWon.p2 + (gameWinner === "p2" ? 1 : 0) },
      [...gameScores, { p1: p1Score, p2: p2Score }]
    );
  };

  const handleCompleteGame = useCallback(() => {
    // Match already decided (e.g. the confirm dialog was cancelled): re-open
    // completion instead of counting the final game twice.
    if (matchWon) {
      const data = buildCompletionData(gamesWon, gameScores);
      if (data && onCompleteMatch) onCompleteMatch(data);
      return;
    }
    if (!gameWinner) return;

    const newGamesWon = {
      p1: gamesWon.p1 + (gameWinner === "p1" ? 1 : 0),
      p2: gamesWon.p2 + (gameWinner === "p2" ? 1 : 0),
    };
    const newGameScores  = [...gameScores, { p1: p1Score, p2: p2Score }];
    const newMatchWon    = newGamesWon.p1 >= gamesToWin || newGamesWon.p2 >= gamesToWin;
    // Don't advance the game counter past the last game when the match is won
    // — prevents "Game 4 of 3" being shown in the header.
    const newCurrentGame = newMatchWon ? currentGame : currentGame + 1;

    setGamesWon(newGamesWon);
    setGameScores(newGameScores);
    setCurrentGame(newCurrentGame);
    setMatchWon(newMatchWon);
    const newGameState = {
      gamesWon: newGamesWon,
      gameScores: newGameScores,
      currentGame: newCurrentGame,
      matchWon: newMatchWon,
    };
    saveBD(matchId, newGameState);

    // Notify parent so it can broadcast the new game state to viewers via WS
    if (onGameComplete) onGameComplete(newGameState);

    // Start the next game at 0–0. When the match is won keep the final
    // game's score on the board (no reset) so it does not read 0–0.
    if (!newMatchWon) {
      const resetFn = onResetSetScore || onResetScore;
      if (resetFn) resetFn({ eventType: "game_start", description: `Game ${newCurrentGame} started`, period: newCurrentGame });
    }

    if (newMatchWon) {
      const completionData = buildCompletionData(newGamesWon, newGameScores);
      setTimeout(() => onCompleteMatch && onCompleteMatch(completionData), 600);
    }
  }, [matchWon, gameWinner, gamesWon, gameScores, p1Score, p2Score, currentGame, gamesToWin, matchId, onResetSetScore, onResetScore, onCompleteMatch, onGameComplete, buildCompletionData]);

  // ── Inline advanced scoring state (for team player picker) ──────────────
  // ActionPanel replaced by unified InlinePlayerGrid + onPlayerSelect popup flow
  const isAdvancedInline = scoringMode === 'advanced' && advancedSupported && !!onPlayerSelect;
  const addBlocked  = !isMatchActive || !!gameWinner || matchWon || viewingGame !== null;
  const p1SubBlocked = !isMatchActive || matchWon || p1Score <= 0 || viewingGame !== null;
  const p2SubBlocked = !isMatchActive || matchWon || p2Score <= 0 || viewingGame !== null;

  // Called as a function so the player icons are not remounted on every render
  const renderActionPanel = (participant, score, players) => {
    if (isReadOnlyMode) return null;

    if (isAdvancedInline) {
      const subBlocked = !isMatchActive || score <= 0 || viewingGame !== null;
      return (
        <div className="vb-adv-inline">
          <InlinePlayerGrid participant={participant} players={players} isDisabled={addBlocked} onPlayerSelect={onPlayerSelect} />
          <button className="vb-deduct-btn" onClick={() => onScoreDecrease(participant, 1)} disabled={subBlocked}>−1</button>
        </div>
      );
    }

    // Basic mode is rendered via the clickable BasicScoreBox in the main JSX below
    return null;
  };

  const viewingScore = viewingGame !== null ? gameScores[viewingGame - 1] : null;

  // Deuce / score display helper
  const isDeuceZone = p1Score >= (ptsToWin - 1) && p2Score >= (ptsToWin - 1) && !gameWinner;
  const rulesLine = isSingleGame
    ? `First to ${ptsToWin} pts · Win by ${winMargin} · Max ${maxPoints}`
    : `First to ${ptsToWin} pts · Win by ${winMargin} · Max ${maxPoints} · ${gamesToWin} games to win`;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <FloatingCard
      variant="gradient"
      size="large"
      className={`score-display-websocket delay-3${isMaximized ? ' score-display-websocket--maximized' : ''}`}
      glow={isMatchActive}
    >

      {/* Header */}
      <div className="score-header-websocket">
        <div className="score-header-top">
          <h3>
            🏸 Badminton
            {!isSingleGame && (
              <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: "0.88em" }}>
                {" — "}
                {viewingGame !== null
                  ? `Game ${viewingGame} (history)`
                  : `Game ${currentGame} of ${totalGames}`}
              </span>
            )}
          </h3>
          <div className="score-header-top-right">
            {!isReadOnlyMode && onScoringModeChange && (
              <ScoringModeToggle
                mode={scoringMode || "basic"}
                onChange={onScoringModeChange}
                sportSupported={advancedSupported}
                disabled={!isMatchActive}
              />
            )}
            <PermissionBadge isReadOnlyMode={isReadOnlyMode} />
            {/* Expand button — available to all users including viewers */}
            <button
              className={`live-ctrl-btn live-ctrl-maximize${isMaximized ? ' active' : ''}`}
              onClick={() => setIsMaximized((m) => !m)}
              title={isMaximized ? 'Restore scoring panel' : 'Expand to full screen'}
            >
              {isMaximized ? '⊡ Restore' : '⛶ Expand'}
            </button>
          </div>
        </div>

        {isMatchActive && onAnnouncerToggleMute && (
          <div className="score-announcer-bar">
            <button
              className={`announcer-btn${announcerMuted ? ' muted' : ''}`}
              onClick={onAnnouncerToggleMute}
              title={announcerMuted ? 'Unmute score announcer' : 'Mute score announcer'}
              aria-label={announcerMuted ? 'Unmute score announcer' : 'Mute score announcer'}
            >
              {announcerMuted ? '🔇' : '🔊'}
            </button>
            <span className="announcer-label">Score Announcer</span>
            <button
              className="announcer-btn"
              onClick={onAnnouncerRepeat}
              title="Repeat last score announcement"
              aria-label="Repeat last score announcement"
            >
              🔁
            </button>
          </div>
        )}

        {!isReadOnlyMode && (
          <div className="live-match-quick-controls">
            {match?.status === "scheduled" && onStartMatch && (
              <button className="live-ctrl-btn live-ctrl-start" onClick={onStartMatch}>
                🎬 Start Match
              </button>
            )}
            {isMatchActive && (
              <>
                <button
                  className={`live-ctrl-btn live-ctrl-timeout${isTimeoutActive ? " active" : ""}`}
                  onClick={onToggleTimeout}
                >
                  {isTimeoutActive ? "▶️ Resume" : "⏸️ Timeout"}
                </button>
                <button
                  className="live-ctrl-btn live-ctrl-reset"
                  onClick={onResetScore}
                  disabled={matchWon || !!gameWinner}
                  style={{ opacity: (matchWon || !!gameWinner) ? 0.35 : 1, cursor: (matchWon || !!gameWinner) ? 'not-allowed' : 'pointer' }}
                  title={(matchWon || !!gameWinner) ? 'Cannot reset a completed game' : 'Reset score to 0 – 0'}
                >
                  🔄 Reset Score
                </button>
                <button className="live-ctrl-btn live-ctrl-complete" onClick={() => {
                  if (!onCompleteMatch) return;
                  // Pass games-won data once the match is decided; otherwise no
                  // arguments (never the click event).
                  const data = completionForHeader();
                  if (data) onCompleteMatch(data); else onCompleteMatch();
                }}>
                  🏁 Complete
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Game navigator tabs (multi-game only) */}
      {!isSingleGame && (
        <>
          <div style={{
            display: "flex", gap: "0.35rem", justifyContent: "center", alignItems: "center",
            marginBottom: "0.45rem", flexWrap: "wrap", padding: "0 0.25rem",
          }}>
            <span style={{ fontSize: "0.72rem", color: "#64748b", marginRight: "0.15rem" }}>Games:</span>

            {Array.from({ length: totalGames }, (_, i) => {
              const gameNum   = i + 1;
              const completed = gameNum < currentGame || (matchWon && gameNum <= gameScores.length);
              const future    = gameNum > currentGame;
              const gs        = gameScores[i];
              const winner    = gameWinners[i];
              const isViewing = viewingGame === gameNum;

              if (future) {
                return (
                  <span key={gameNum} style={{ padding: "0.18rem 0.45rem", borderRadius: "0.3rem", background: "rgba(0,0,0,0.15)", color: "#475569", fontSize: "0.68rem", border: "1px solid rgba(71,85,105,0.2)" }}>
                    G{gameNum}
                  </span>
                );
              }

              if (completed) {
                const dotColor = winner === "p1" ? "#22c55e" : winner === "p2" ? "#ef4444" : "#f59e0b";
                return (
                  <button
                    key={gameNum}
                    title={`Game ${gameNum}: ${p1Name} ${gs?.p1}–${gs?.p2} ${p2Name}`}
                    onClick={() => setViewingGame(isViewing ? null : gameNum)}
                    style={{
                      padding: "0.18rem 0.5rem", borderRadius: "0.3rem",
                      background: isViewing ? "rgba(99,102,241,0.3)" : "rgba(0,0,0,0.25)",
                      border: `1px solid ${isViewing ? "rgba(99,102,241,0.6)" : "rgba(100,116,139,0.4)"}`,
                      color: isViewing ? "#a78bfa" : "#94a3b8",
                      fontSize: "0.68rem", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: "0.2rem",
                    }}
                  >
                    <span style={{ color: dotColor, fontSize: "0.55rem" }}>●</span>
                    G{gameNum} {gs?.p1}–{gs?.p2}
                    {isViewing && <span> ✕</span>}
                  </button>
                );
              }

              // Current live game
              return (
                <button
                  key={gameNum}
                  title="Current live game — click to return to live view"
                  onClick={() => setViewingGame(null)}
                  style={{
                    padding: "0.18rem 0.5rem", borderRadius: "0.3rem",
                    background: viewingGame === null ? "rgba(34,197,94,0.2)" : "rgba(0,0,0,0.2)",
                    border: `1px solid ${viewingGame === null ? "rgba(34,197,94,0.5)" : "rgba(100,116,139,0.3)"}`,
                    color: viewingGame === null ? "#22c55e" : "#94a3b8",
                    fontSize: "0.68rem", cursor: "pointer",
                    fontWeight: viewingGame === null ? 700 : 400,
                    display: "flex", alignItems: "center", gap: "0.2rem",
                  }}
                >
                  {viewingGame === null && <span style={{ color: "#ef4444", fontSize: "0.5rem" }}>●</span>}
                  G{gameNum}
                  {viewingGame !== null && <span style={{ fontSize: "0.55rem", opacity: 0.7 }}> ▶</span>}
                </button>
              );
            })}

            <span style={{ marginLeft: "0.4rem", fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>
              {gamesWon.p1}–{gamesWon.p2}
            </span>
          </div>

          <div style={{ textAlign: "center", fontSize: "0.68rem", color: "#64748b", marginBottom: "0.65rem" }}>
            {rulesLine}
          </div>
        </>
      )}

      {/* Single-game rules line */}
      {isSingleGame && (
        <div style={{ textAlign: "center", fontSize: "0.68rem", color: "#64748b", marginBottom: "0.65rem" }}>
          {rulesLine}
        </div>
      )}

      {/* Historical game detail */}
      {viewingGame !== null && viewingScore ? (
        <div style={{ padding: "1rem 1.25rem", background: "rgba(0,0,0,0.2)", borderRadius: "0.5rem", marginBottom: "0.75rem" }}>
          <div style={{ textAlign: "center", color: "#64748b", fontSize: "0.75rem", marginBottom: "0.65rem" }}>
            Game {viewingGame} — Final Score
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "2rem" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#e2e8f0", fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.25rem" }}>{p1Name}</div>
              <div style={{ fontSize: "3rem", fontWeight: 800, lineHeight: 1, color: viewingScore.p1 > viewingScore.p2 ? "#22c55e" : "#94a3b8" }}>
                {viewingScore.p1}
              </div>
              {viewingScore.p1 > viewingScore.p2 && (
                <div style={{ fontSize: "0.65rem", color: "#22c55e", marginTop: "0.2rem" }}>✓ Won Game</div>
              )}
            </div>
            <div style={{ fontSize: "1.5rem", color: "#334155", fontWeight: 700 }}>–</div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#e2e8f0", fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.25rem" }}>{p2Name}</div>
              <div style={{ fontSize: "3rem", fontWeight: 800, lineHeight: 1, color: viewingScore.p2 > viewingScore.p1 ? "#22c55e" : "#94a3b8" }}>
                {viewingScore.p2}
              </div>
              {viewingScore.p2 > viewingScore.p1 && (
                <div style={{ fontSize: "0.65rem", color: "#22c55e", marginTop: "0.2rem" }}>✓ Won Game</div>
              )}
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: "0.85rem" }}>
            <button
              onClick={() => setViewingGame(null)}
              style={{ padding: "0.3rem 0.9rem", fontSize: "0.75rem", background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.4)", color: "#22c55e", borderRadius: "0.4rem", cursor: "pointer" }}
            >
              ▶ Back to Live Scoring
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Game won banner */}
          {gameWinner && isMatchActive && !isReadOnlyMode && (
            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap", padding: "0.75rem 1rem", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.4)", borderRadius: "0.5rem", marginBottom: "0.75rem" }}>
              <span style={{ color: "#22c55e", fontWeight: 700 }}>
                🏆 {gameWinner === "p1" ? p1Name : p2Name} won{isSingleGame ? " the game" : ` Game ${currentGame}`}!
                {" "}({p1Score}–{p2Score})
              </span>
              {!isSingleGame && !matchWon && currentGame < totalGames && gamesWon[gameWinner] + 1 < gamesToWin ? (
                <button
                  onClick={handleCompleteGame}
                  style={{ padding: "0.4rem 1.1rem", background: "rgba(34,197,94,0.25)", border: "1px solid #22c55e", color: "#22c55e", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}
                >
                  ✅ Complete Game {currentGame} — Start Game {currentGame + 1}
                </button>
              ) : (
                <button
                  onClick={isSingleGame ? () => onCompleteMatch && onCompleteMatch() : handleCompleteGame}
                  style={{ padding: "0.4rem 1.1rem", background: "rgba(34,197,94,0.25)", border: "1px solid #22c55e", color: "#22c55e", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}
                >
                  🏆 Complete Match
                </button>
              )}
            </div>
          )}

          {/* Deuce notice */}
          {isDeuceZone && !gameWinner && (
            <div style={{ textAlign: "center", fontSize: "0.75rem", color: "#f59e0b", marginBottom: "0.5rem", fontWeight: 600 }}>
              ⚡ Deuce — play to {maxPoints < 999 ? `${maxPoints} max` : "2-point lead"}
            </div>
          )}

          {/* Live scoring display — single-row, team names above cards */}
          <div className="participants-websocket">
            <div className="vb-participant-col">
              <div className="vb-team-name">
                {p1Name}
                {!isSingleGame && <span className="participant-type">Games Won: {gamesWon.p1}</span>}
              </div>
              {!isReadOnlyMode && !isAdvancedInline ? (
                <BasicScoreBox
                  participantClassName="participant-1"
                  score={p1Score}
                  disabled={addBlocked}
                  subDisabled={p1SubBlocked}
                  onIncrease={() => onScoreIncrease("participant1", 1)}
                  onDecrease={() => onScoreDecrease("participant1", 1)}
                  addLabel={`Add 1 point for ${p1Name}`}
                  subLabel={`Subtract 1 point for ${p1Name}`}
                />
              ) : (
                <div className="participant-websocket participant-1">
                  <div className="participant-score-large">{p1Score}</div>
                  {renderActionPanel("participant1", p1Score, p1Players)}
                </div>
              )}
            </div>

            <div className="vs-divider-websocket">
              <div className="vs-circle-websocket">
                {isSingleGame ? "VS" : `G${currentGame}`}
              </div>
              <div className="vs-duration" style={{ fontSize: "0.85rem", color: "#64748b", whiteSpace: "nowrap", textAlign: "center" }}>
                {isMatchActive
                  ? "In Progress"
                  : match?.status === "scheduled"
                    ? "Scheduled"
                    : (match?.status?.replace(/_/g, " ") ?? "—")}
              </div>
              {matchDuration && (
                <div style={{ fontSize: "0.85rem", fontWeight: 600, color: isTimeoutActive ? "#f59e0b" : "#64748b", whiteSpace: "nowrap", textAlign: "center", marginTop: "0.2rem" }}>
                  ⏱ {matchDuration}
                </div>
              )}
            </div>

            <div className="vb-participant-col">
              <div className="vb-team-name">
                {p2Name}
                {!isSingleGame && <span className="participant-type">Games Won: {gamesWon.p2}</span>}
              </div>
              {!isReadOnlyMode && !isAdvancedInline ? (
                <BasicScoreBox
                  participantClassName="participant-2"
                  score={p2Score}
                  disabled={addBlocked}
                  subDisabled={p2SubBlocked}
                  onIncrease={() => onScoreIncrease("participant2", 1)}
                  onDecrease={() => onScoreDecrease("participant2", 1)}
                  addLabel={`Add 1 point for ${p2Name}`}
                  subLabel={`Subtract 1 point for ${p2Name}`}
                />
              ) : (
                <div className="participant-websocket participant-2">
                  <div className="participant-score-large">{p2Score}</div>
                  {renderActionPanel("participant2", p2Score, p2Players)}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </FloatingCard>
  );
};

export default BadmintonScoreboard;
